// The rows of «Cotizaciones y pagos» (Diagnostic Intake records) when the app's data changes under it.
//
// The app's data (useAppData) is replaced by every local update and every background reload. A quote created or
// edited in this screen must survive that: an update that does not know the new record, or a reload that started
// before the save and answers after it, never drops the record nor brings back the fields it had before. Only what
// was written here is kept: records created in this session and the fields saved here, until a reload confirms them.

const linkIds = (v) => v.map(x => (x && typeof x === 'object' ? x.id ?? x.name ?? x : x));
const norm = (v) => (Array.isArray(v) ? JSON.stringify(linkIds(v)) : v && typeof v === 'object' ? JSON.stringify(v.name ?? v) : JSON.stringify(v ?? null));
const sameValue = (a, b) => norm(a) === norm(b);

/**
 * incoming: the records the app now has; prev: the rows shown; local: { created: Set<id>, patches: Map<id, fields> }.
 * Returns the rows: the incoming ones with the local patches on top, plus the records created here that the incoming
 * list does not have yet (never any other: a record the app no longer has and that was not created here goes away).
 */
export function mergeIntakeRows(incoming = [], prev = [], local = {}) {
  const created = local.created || new Set();
  const patches = local.patches || new Map();
  const prevById = new Map(prev.map(r => [r.id, r]));
  const seen = new Set();
  const out = incoming.map(r => {
    seen.add(r.id);
    const before = prevById.get(r.id);
    const patch = patches.get(r.id);
    const createdTime = r.createdTime || before?.createdTime;
    if (!patch && createdTime === r.createdTime) return r;
    return { ...r, ...(createdTime ? { createdTime } : {}), fields: { ...(r.fields || {}), ...(patch || {}) } };
  });
  for (const r of prev) {
    if (seen.has(r.id) || !created.has(r.id)) continue;
    const patch = patches.get(r.id);
    out.push(patch ? { ...r, fields: { ...(r.fields || {}), ...patch } } : r);
  }
  return out;
}

/** The local patches a reload already confirms (every saved field has that value) stop applying; the rest stay. */
export function pendingPatches(incoming = [], patches = new Map()) {
  const byId = new Map(incoming.map(r => [r.id, r]));
  const out = new Map();
  for (const [id, fields] of patches) {
    const r = byId.get(id);
    const left = Object.fromEntries(Object.entries(fields).filter(([k, v]) => !r || !sameValue(r.fields?.[k], v)));
    if (Object.keys(left).length) out.set(id, left);
  }
  return out;
}
